import { useMemo, useRef } from 'react';
import { Card } from 'utils/customTypes';

const getCardDataSignature = (card: Card): string => {
  const identifier = card.id ?? card.lid ?? '';
  return `${identifier}:${card.cardTypeId}`;
};

const createCardsSignature = (cards: Card[], dashboardId: string): string => {
  if (!cards || cards.length === 0) {
    return '';
  }

  const signatures = cards.map(getCardDataSignature).sort().join(':');
  return `${signatures}:${dashboardId}`;
};

const getFilterSignature = ([key, value]: [string, unknown]): string => {
  return `${key}:${JSON.stringify(value)}`;
};

const createFiltersSignature = (
  filters: Record<string, unknown>,
  dashboardId: string
): string => {
  if (!filters || Object.keys(filters).length === 0) {
    return '';
  }

  const signatures = Object.entries(filters)
    .map(getFilterSignature)
    .sort()
    .join(':');
  return `${signatures}:${dashboardId}`;
};

export const useCardsDataDependency = (
  dashboardId: string,
  cards: Card[],
  filters: Record<string, unknown>
): {
  cardsSignature: string;
  filtersSignature: string;
} => {
  const previousCardsSignatureRef = useRef<string>('');
  const previousFiltersSignatureRef = useRef<string>('');

  const cardsSignature = useMemo<string>(() => {
    const newSignature = createCardsSignature(cards, dashboardId);

    if (newSignature !== previousCardsSignatureRef.current) {
      previousCardsSignatureRef.current = newSignature;
      return newSignature;
    }

    return previousCardsSignatureRef.current;
  }, [cards, dashboardId]);

  const filtersSignature = useMemo<string>(() => {
    const newSignature = createFiltersSignature(filters, dashboardId);

    if (newSignature !== previousFiltersSignatureRef.current) {
      previousFiltersSignatureRef.current = newSignature;
      return newSignature;
    }

    return previousFiltersSignatureRef.current;
  }, [filters, dashboardId]);

  return useMemo(
    () => ({
      cardsSignature,
      filtersSignature,
    }),
    [cardsSignature, filtersSignature]
  );
};
